import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { IconButton, Tooltip, Snackbar, Alert, useTheme } from '@mui/material';
import { FileCopy as FileCopyIcon } from '@mui/icons-material';
import { ThemeMode } from '../../abelcommon';
import { useTranslation } from 'react-i18next';

interface CodeBlockProps {
  inline: boolean;
  className: string;
  children: React.ReactNode;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ inline, className, children }) => {
  const theme = useTheme();
  const codeString = String(children).replace(/\n$/, '');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const { t } = useTranslation();

  const handleCopy = (code: string) => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setOpenSnackbar(true);
      })
      .catch((err) => {
        console.error(t('codeBlockPage.failToCopyCode'), err);
      });
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  if (inline) {
    return <code>{children}</code>;
  }

  return (
    <div style={{ position: 'relative' }}>
      <CopyToClipboard text={codeString} onCopy={() => handleCopy(codeString)}>
        <Tooltip title={t('codeBlockPage.copyCode')} placement="top">
          <IconButton
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              zIndex: 1,
            }}
          >
            <FileCopyIcon />
          </IconButton>
        </Tooltip>
      </CopyToClipboard>
      <div
        style={{
          padding: '10px',
          borderRadius: '5px',
          ...(theme.palette.mode === ThemeMode.Light ? { backgroundColor: '#E8E8E8' } : { backgroundColor: '#0F1F33' }),
        }}
        className={className}
      >
        <code>{codeString}</code>
      </div>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={t('codeBlockPage.codeCopiedToClipboard')}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          {t('codeBlockPage.codeCopiedToClipboard')}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CodeBlock;
